import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowDownAZ, ArrowUpZA, ChevronDown, ArrowUp, ArrowDown, Filter } from "lucide-react";

const EMPTY = "—";
const isActive = (s) => !!s && (s instanceof Set ? s.size > 0 : !!(s.from || s.to));
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DATE_PRESETS = [["all", "All dates"], ["this-month", "This month"], ["last-month", "Last month"], ["this-quarter", "This quarter"], ["this-year", "This year"], ["last-year", "Last year"]];
function presetRange(key) {
  const t = new Date(), y = t.getFullYear(), m = t.getMonth();
  if (key === "this-month") return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  if (key === "last-month") return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (key === "this-quarter") { const q = Math.floor(m / 3); return { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) }; }
  if (key === "this-year") return { from: `${y}-01-01`, to: `${y}-12-31` };
  if (key === "last-year") return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  if (key.startsWith("year-")) return { from: `${key.slice(5)}-01-01`, to: `${key.slice(5)}-12-31` };
  return { from: "", to: "" };
}
const norm = (v) => (v === null || v === undefined || v === "" ? EMPTY : String(v));

// cols: { key: { value: row => display string, sort?: row => comparable } }
export function useColumnFilters(rows, cols, defaultSort) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(defaultSort || null);

  const view = useMemo(() => {
    const active = Object.entries(filters).filter(([, s]) => isActive(s));
    let out = rows.filter((r) => active.every(([k, s]) => {
      if (s instanceof Set) return s.has(norm(cols[k].value(r)));
      const v = (cols[k].value(r) || "").slice(0, 10);
      return !!v && (!s.from || v >= s.from) && (!s.to || v <= s.to);
    }));
    if (sort && cols[sort.key]) {
      const get = cols[sort.key].sort || ((r) => norm(cols[sort.key].value(r)).toLowerCase());
      const dir = sort.dir === "desc" ? -1 : 1;
      out = [...out].sort((a, b) => {
        const x = get(a), y = get(b);
        if (x === y) return 0;
        if (x === "" || x == null) return 1;
        if (y === "" || y == null) return -1;
        return x > y ? dir : -dir;
      });
    }
    return out;
  }, [rows, cols, filters, sort]);

  const setFilter = (key, set) => setFilters((f) => ({ ...f, [key]: set }));
  const anyActive = Object.values(filters).some(isActive);
  return { view, filters, setFilter, sort, setSort, anyActive, clearAll: () => setFilters({}) };
}

// Excel-style header: sort A→Z / Z→A + multi-select tick list of distinct values.
export function HeaderFilter({ label, colKey, rows, cols, ctl, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const col = cols[colKey];
  const options = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => { const v = norm(col.value(r)); if (!seen.has(v)) seen.set(v, col.sort ? col.sort(r) : v.toLowerCase()); });
    return [...seen.entries()].sort((a, b) => (a[0] === EMPTY ? 1 : b[0] === EMPTY ? -1 : a[1] > b[1] ? 1 : a[1] < b[1] ? -1 : 0)).map(([v]) => v);
  }, [rows, col]);
  const sel = ctl.filters[colKey];
  const active = isActive(sel);
  const sorted = ctl.sort?.key === colKey ? ctl.sort.dir : null;
  const shown = options.filter((o) => o.toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => { const n = new Set(sel || []); n.has(v) ? n.delete(v) : n.add(v); ctl.setFilter(colKey, n); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-[var(--ink)] transition-colors ${active || sorted ? "text-[var(--navy)] font-semibold" : ""}`} style={{ font: "inherit" }}>
          {label}
          {sorted === "asc" && <ArrowUp size={11} />}{sorted === "desc" && <ArrowDown size={11} />}
          {active ? <Filter size={10} className="fill-current" /> : <ChevronDown size={11} className="opacity-60" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2 text-sm normal-case tracking-normal" data-testid={`${testid}-menu`} onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1 mb-2">
          <button data-testid={`${testid}-sort-asc`} onClick={() => { ctl.setSort({ key: colKey, dir: "asc" }); setOpen(false); }} className={`flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-sm border transition-colors ${sorted === "asc" ? "border-[var(--navy)] bg-[var(--navy-soft)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}><ArrowDownAZ size={13} /> A→Z</button>
          <button data-testid={`${testid}-sort-desc`} onClick={() => { ctl.setSort({ key: colKey, dir: "desc" }); setOpen(false); }} className={`flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-sm border transition-colors ${sorted === "desc" ? "border-[var(--navy)] bg-[var(--navy-soft)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}><ArrowUpZA size={13} /> Z→A</button>
        </div>
        {col.date ? <DateMenu rows={rows} col={col} sel={sel} set={(v) => ctl.setFilter(colKey, v)} testid={testid} /> : <>
        {options.length > 8 && <input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="input-slim w-full text-xs mb-2" />}
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {shown.map((o) => (
            <label key={o} className="flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-[var(--bg)] cursor-pointer text-xs">
              <input type="checkbox" data-testid={`${testid}-opt-${o}`} checked={!!sel?.has(o)} onChange={() => toggle(o)} className="accent-[var(--navy)] w-3.5 h-3.5" />
              <span className="truncate">{o}</span>
            </label>
          ))}
          {shown.length === 0 && <div className="text-xs text-[var(--ink-2)] p-1.5">No values</div>}
        </div>
        </>}
        <div className="flex justify-between mt-2 pt-2 border-t border-[var(--line)]">
          <button data-testid={`${testid}-clear`} onClick={() => ctl.setFilter(colKey, col.date ? null : new Set())} className="text-xs text-[var(--ink-2)] underline hover:text-[var(--ink)]">Clear</button>
          <button data-testid={`${testid}-done`} onClick={() => setOpen(false)} className="text-xs font-semibold text-[var(--navy)]">Done</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Date column menu: presets, Year YYYY (from the data), custom range — mirrors Xero Invoices date filter.
function DateMenu({ rows, col, sel, set, testid }) {
  const years = useMemo(() => [...new Set(rows.map((r) => (col.value(r) || "").slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))].sort().reverse(), [rows, col]);
  const cur = sel?.preset || (sel && (sel.from || sel.to) ? "custom" : "all");
  const pick = (key) => set(key === "all" ? null : key === "custom" ? { preset: "custom", from: sel?.from || "", to: sel?.to || "" } : { preset: key, ...presetRange(key) });
  const item = (key, label) => (
    <label key={key} className="flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-[var(--bg)] cursor-pointer text-xs">
      <input type="radio" data-testid={`${testid}-preset-${key}`} checked={cur === key} onChange={() => pick(key)} className="accent-[var(--navy)] w-3.5 h-3.5" />
      <span>{label}</span>
    </label>
  );
  return (
    <div className="max-h-72 overflow-y-auto space-y-0.5">
      {DATE_PRESETS.map(([k, l]) => item(k, l))}
      {years.length > 0 && <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] px-1.5 pt-1.5">Year</div>}
      {years.map((y) => item(`year-${y}`, `Year ${y}`))}
      {item("custom", "Custom range")}
      {cur === "custom" && (
        <div className="flex gap-1 px-1.5 pb-1">
          <input type="date" data-testid={`${testid}-from`} value={sel?.from || ""} onChange={(e) => set({ preset: "custom", from: e.target.value, to: sel?.to || "" })} className="input-slim !text-[10px] !px-1 w-full" />
          <input type="date" data-testid={`${testid}-to`} value={sel?.to || ""} onChange={(e) => set({ preset: "custom", from: sel?.from || "", to: e.target.value })} className="input-slim !text-[10px] !px-1 w-full" />
        </div>
      )}
    </div>
  );
}
